import {useEffect, useRef, useState} from 'react';

const getWidth = () =>
  window.innerWidth || document.documentElement.clientWidth || document.body.clientWidth;

function useCurrentWidth() {
  const [width, setWidth] = useState(getWidth());
  useEffect(() => {
    const resizeListener = () => {
      setWidth(getWidth());
    };
    window.addEventListener('resize', evite.debounce(resizeListener, 250));

    return () => {
      window.removeEventListener('resize', evite.debounce(resizeListener, 250));
    };
  }, []);

  return width;
}

export const useFitContainer = (options) => {
  const {startingScale = 1.0, stepSize = 0.05} = options;

  const ref = useRef(null);

  const width = useCurrentWidth();

  const [state, setState] = useState({
    scale: startingScale,
    prevScale: options.minScale || 0.5,
    isDone: false,
    minScale: options.minScale || 0.5,
    maxScale: options.maxScale || 1.0,
    prevWidth: width,
  });
  const {scale, prevScale, minScale, maxScale, isDone, prevWidth} = state;
  // biome-ignore lint/correctness/useExhaustiveDependencies: deps
  useEffect(() => {
    if (width !== prevWidth) {
      // Reset to the original settings and rerun the size search from the beginning when the window chanes size
      // TODO: Need to add a debounce here if we ever use this on desktop
      setState({
        scale: startingScale,
        prevScale: options.minScale || 0.5,
        isDone: false,
        minScale: options.minScale || 0.5,
        maxScale: options.maxScale || 1.0,
        prevWidth: width,
      });
      return;
    }
    if (isDone) return;

    const newIsDone = Math.abs(scale - prevScale) <= stepSize;
    const isOverflow = !!ref.current && ref.current.scrollHeight > ref.current.offsetHeight;

    // return if the scale has been adjusted "enough" (change within RESOLUTION)
    // reduce scale by one increment if it's overflowing
    if (newIsDone) {
      if (isOverflow) {
        const scaleNew = prevScale < scale ? prevScale : scale - (prevScale - scale);
        setState({
          scale: scaleNew,
          maxScale,
          minScale,
          prevScale: scale,
          isDone: newIsDone,
          prevWidth: width,
        });
      }
      return;
    }

    // binary search to adjust scale
    let newMax = maxScale;
    let newMin = minScale;
    let delta = Math.abs(prevScale - scale);
    if (isOverflow) {
      delta *= -1;
      newMax = Math.min(maxScale, scale);
    } else {
      newMin = Math.max(minScale, scale);
    }

    setState({
      scale: Math.min(scale + delta / 2, options.maxScale || 1.0),
      maxScale: newMax,
      minScale: newMin,
      prevScale: scale,
      isDone: newIsDone,
      prevWidth: width,
    });
  }, [scale, prevScale, ref, width]);

  return {scale, isDone, ref};
};
